/**
 * Helpers de adjuntos de listing (UF `user_lot_attachments`, lot-content,
 * parcela CAD `custom_parcel_attachments`). Misma shape y tope.
 * Separado de fotos y videos YouTube. Tope: 2 — JPEG/PNG ≤8 MB, PDF ≤10 MB.
 */

export const UF_MAX_ATTACHMENTS = 2;

export const UF_ATTACHMENT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const UF_ATTACHMENT_PDF_MAX_BYTES = 10 * 1024 * 1024;

const IMAGE_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png']);
const PDF_TYPES = new Set(['application/pdf']);

export const UF_ATTACHMENT_ACCEPT = 'image/jpeg,image/jpg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

export const UF_ATTACHMENT_LIMIT_TOAST = {
  title: 'Límite alcanzado',
  message: `Máximo ${UF_MAX_ATTACHMENTS} adjuntos.`,
};

export const UF_ATTACHMENT_INVALID_TOAST = {
  title: 'Archivo no permitido',
  message: 'Solo JPEG/PNG (hasta 8 MB) o PDF (hasta 10 MB).',
};

const UUID_STEM_RE = /^[0-9a-f]{32}$/i;

/**
 * @param {string} contentType
 * @returns {'image'|'pdf'|null}
 */
export function getUfAttachmentKind(contentType) {
  const type = String(contentType || '').trim().toLowerCase();
  if (IMAGE_TYPES.has(type)) return 'image';
  if (PDF_TYPES.has(type)) return 'pdf';
  return null;
}

/**
 * @param {string} contentType
 * @returns {number|null}
 */
export function getUfAttachmentMaxBytes(contentType) {
  const kind = getUfAttachmentKind(contentType);
  if (kind === 'image') return UF_ATTACHMENT_IMAGE_MAX_BYTES;
  if (kind === 'pdf') return UF_ATTACHMENT_PDF_MAX_BYTES;
  return null;
}

/**
 * Basename de path/URL/s3_key (sin query/hash).
 * @param {unknown} raw
 * @returns {string}
 */
export function basenameFromPathOrUrl(raw) {
  const text = String(raw || '').trim();
  if (!text) return '';
  let pathPart = text;
  try {
    if (/^https?:\/\//i.test(text)) {
      pathPart = new URL(text).pathname;
    }
  } catch {
    pathPart = text.split(/[?#]/)[0];
  }
  pathPart = pathPart.split(/[?#]/)[0];
  const segments = pathPart.split('/').filter(Boolean);
  if (segments.length === 0) return '';
  let name = segments[segments.length - 1];
  try {
    name = decodeURIComponent(name);
  } catch {
    // keep raw segment
  }
  return name.trim();
}

/**
 * Nombre para UI: file_name / filename / original_name / name, luego
 * basename de s3_key o url. Stems UUID hex (API actual) se ignoran.
 * @param {object} item
 * @param {{ fallback?: string }} [opts]
 * @returns {string}
 */
export function resolveAttachmentDisplayName(item, opts = {}) {
  const fallback = opts.fallback != null ? opts.fallback : 'Documento PDF';
  if (!item || typeof item !== 'object') return fallback;

  const candidates = [
    item.file_name,
    item.filename,
    item.original_name,
    item.originalName,
    item.fileName,
    item.name,
  ];
  for (const candidate of candidates) {
    const text = typeof candidate === 'string' ? candidate.trim() : '';
    if (text) return text;
  }

  const fromKey = basenameFromPathOrUrl(item.s3_key || item.s3Key);
  const fromUrl = basenameFromPathOrUrl(item.url);
  for (const name of [fromKey, fromUrl]) {
    if (!name) continue;
    const stem = name.replace(/\.[^.]+$/, '');
    // API guarda `{uuid32}.{ext}` — no es un nombre humano útil.
    if (UUID_STEM_RE.test(stem)) continue;
    return name;
  }

  return fallback;
}

/**
 * @param {File|Blob} file
 * @returns {{ ok: true, contentType: string, kind: 'image'|'pdf' }
 *   | { ok: false, message: string }}
 */
export function validateUfAttachmentFile(file) {
  if (!file) {
    return { ok: false, message: 'Elegí un archivo para adjuntar.' };
  }
  const contentType = String(file.type || '').trim().toLowerCase();
  const kind = getUfAttachmentKind(contentType);
  if (!kind) {
    return {
      ok: false,
      message: 'Formato no permitido. Solo JPEG, PNG o PDF.',
    };
  }
  const maxBytes = getUfAttachmentMaxBytes(contentType);
  const size = Number(file.size) || 0;
  if (size < 1) {
    return { ok: false, message: 'El archivo está vacío.' };
  }
  if (size > maxBytes) {
    const maxMb = maxBytes / (1024 * 1024);
    return {
      ok: false,
      message:
        kind === 'image'
          ? `La imagen no puede superar ${maxMb} MB.`
          : `El PDF no puede superar ${maxMb} MB.`,
    };
  }
  return { ok: true, contentType, kind };
}

/**
 * @param {unknown} attachments
 * @returns {Array<{ id?: string, kind: 'image'|'pdf', content_type?: string, url: string, file_name?: string, sort_order?: number, created_at?: string }>}
 */
export const normalizeListingAttachments = (attachments) => {
  const list = Array.isArray(attachments) ? attachments : [];
  return list
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const url = typeof item.url === 'string' ? item.url.trim() : '';
      if (!url) return null;
      const contentType = String(item.content_type || item.contentType || '').trim().toLowerCase();
      const kindFromField = item.kind === 'pdf' || item.kind === 'image' ? item.kind : null;
      const kind = kindFromField || getUfAttachmentKind(contentType) || (
        /\.pdf(\?|$)/i.test(url) ? 'pdf' : 'image'
      );
      const fileName = resolveAttachmentDisplayName(
        item,
        { fallback: '' },
      );
      const normalized = {
        ...item,
        kind,
        content_type: contentType || (kind === 'pdf' ? 'application/pdf' : 'image/jpeg'),
        url,
      };
      if (fileName) {
        normalized.file_name = fileName;
      }
      return normalized;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const orderA = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 0;
      const orderB = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 0;
      if (orderA !== orderB) return orderA - orderB;
      const ta = a?.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b?.created_at ? new Date(b.created_at).getTime() : 0;
      if (ta !== tb) return ta - tb;
      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });
};

export const extractAttachmentsFromApiPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.attachments)) return payload.attachments;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

export const normalizeListingAttachmentsFromPayload = (payload) => (
  normalizeListingAttachments(extractAttachmentsFromApiPayload(payload))
);

export const resolveAttachmentApiMessage = (res) => (
  res?.message || res?.error || res?.detail || ''
);

export const isAttachmentLimitError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('límite')
    || text.includes('limite')
    || text.includes('limit')
    || /m[aá]ximo\s*2|de\s*2/.test(text)
  );
};

export const isAttachmentDuplicateError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('ya está')
    || text.includes('ya esta')
    || text.includes('already')
    || text.includes('duplicad')
  );
};

export const isAttachmentTypeOrSizeError = (httpStatus, message = '') => {
  const text = String(message).toLowerCase();
  return httpStatus === 400 && (
    text.includes('contenttype')
    || text.includes('content-type')
    || text.includes('contentlength')
    || text.includes('content-length')
    || text.includes('mime')
    || text.includes('tamaño')
    || text.includes('tamano')
    || text.includes('size')
    || text.includes('inválid')
    || text.includes('invalid')
  ) && !isAttachmentLimitError(httpStatus, message) && !isAttachmentDuplicateError(httpStatus, message);
};
